/**
 * Alertas de Cuidado: avisa o time quando alguém muda de comportamento (gasto
 * que acelerou, exame de investigação, medicamento de alto custo) ANTES de o
 * caso virar problema financeiro. Regras em backend/app/api/alertas_cuidado.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BellRing, TrendingUp, Microscope, Pill, ChevronDown, ChevronUp, Loader, RefreshCw, Clock, ExternalLink, BedDouble,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api, { alertasCuidadoAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import InfoTip from '../../components/InfoTip';

const COR = '#c0392b';
const fmtR = (v) => `R$ ${Number(v || 0).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`;
const fmtData = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : '—');
const fmtDataHora = (iso) => (iso ? `${fmtData(iso)} ${iso.slice(11, 16)}` : '—');

export const SINAIS = {
  gasto: { rot: 'Gasto acelerou', icon: TrendingUp, cor: '#b71c1c', bg: '#ffebee' },
  investigacao: { rot: 'Investigação', icon: Microscope, cor: '#6a1b9a', bg: '#f3e5f5' },
  alto_custo: { rot: 'Alto custo começou', icon: Pill, cor: '#e65100', bg: '#fff3e0' },
};
const ESTADOS = {
  novo: { rot: 'Novo', cor: '#c0392b', bg: '#fdecea' },
  acompanhando: { rot: 'Acompanhando', cor: '#1565c0', bg: '#e3f2fd' },
  descartado: { rot: 'Descartado', cor: '#546e7a', bg: '#eceff1' },
  resolvido: { rot: 'Resolvido', cor: '#2e7d32', bg: '#e8f5e9' },
};

const TEXTO_INFO = 'De onde vem: contas médicas já apresentadas (utilização), recalculadas todo dia depois da carga.\n\n'
  + 'Gasto acelerou: gasto por mês dos últimos 60 dias acima de R$ 5 mil e pelo menos 4 vezes a média da própria pessoa nos 12 meses anteriores.\n\n'
  + 'Investigação: biópsia, punção, imunoistoquímica, PET, mielograma ou implante de cateter de longa permanência nos últimos 60 dias. Preventivo de rotina não entra.\n\n'
  + 'Alto custo começou: primeira aplicação de medicamento oncológico ou imunobiológico, sem uso no ano anterior.\n\n'
  + 'Limite: o mês mais recente ainda está incompleto (conta chega depois do atendimento) e sabemos que o exame foi feito, não o resultado. É aviso para agir cedo, não diagnóstico.';

export function MotivosAlerta({ a, compacto = false }) {
  const linhas = [];
  if (a.sinais.includes('gasto')) {
    linhas.push(<>Gasto subiu para <strong>{fmtR(a.gasto_recente_mes)}/mês</strong> nos últimos 60 dias (antes: {fmtR(a.gasto_antes_mes)}/mês).</>);
  }
  if (a.sinais.includes('investigacao')) {
    const itens = compacto ? a.investigacao.slice(-3) : a.investigacao;
    linhas.push(<>Exames de investigação: {itens.map((x, i) => (
      <span key={i}>{i > 0 && '; '}{x.descricao.toLowerCase()} ({fmtData(x.data)})</span>
    ))}{compacto && a.investigacao.length > 3 ? ` e mais ${a.investigacao.length - 3}` : ''}.</>);
    if (a.historico_onco) linhas.push(<strong style={{ color: '#6a1b9a' }}>Já tratou câncer nos últimos 2 anos: pode ser retorno da doença.</strong>);
  }
  if (a.sinais.includes('alto_custo')) {
    linhas.push(<>Começou medicamento de alto custo: {a.alto_custo.map((x, i) => (
      <span key={i}>{i > 0 && '; '}{x.descricao.split(' - ')[0]} ({x.vezes}x, {fmtR(x.valor)}, desde {fmtData(x.data)})</span>
    ))}.</>);
  }
  return (
    <ul style={{ margin: 0, paddingLeft: 18, fontSize: '0.78rem', color: '#37474f', lineHeight: 1.55 }}>
      {linhas.map((l, i) => <li key={i}>{l}</li>)}
    </ul>
  );
}

function Selo({ s }) {
  const c = SINAIS[s]; if (!c) return null; const Ic = c.icon;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.64rem', fontWeight: 700, color: c.cor, background: c.bg, padding: '2px 8px', borderRadius: 6, whiteSpace: 'nowrap' }}>
      <Ic size={11} /> {c.rot}
    </span>
  );
}

function Acoes({ a, podeMarcar, onSalvo }) {
  const [nota, setNota] = useState(a.nota || '');
  const [salvando, setSalvando] = useState(false);
  const [hist, setHist] = useState(null);
  useEffect(() => {
    alertasCuidadoAPI.historico(a.matricula).then((r) => setHist(r.data.historico)).catch(() => setHist([]));
  }, [a.matricula]);
  const marcar = async (estado) => {
    setSalvando(true);
    try {
      await alertasCuidadoAPI.acompanhar(a.matricula, { estado, nota });
      toast.success(`Marcado como ${ESTADOS[estado].rot.toLowerCase()}`);
      onSalvo();
    } catch (e) { toast.error(e.response?.data?.detail || 'Não foi possível salvar'); }
    finally { setSalvando(false); }
  };
  const b = (cor) => ({ fontSize: '0.74rem', fontWeight: 600, padding: '6px 12px', borderRadius: 8, cursor: salvando ? 'default' : 'pointer', border: `1px solid ${cor}`, background: '#fff', color: cor });
  return (
    <div>
      <MotivosAlerta a={a} />
      {podeMarcar && (
        <div style={{ marginTop: 12 }}>
          <textarea value={nota} onChange={(e) => setNota(e.target.value)} rows={2} maxLength={2000}
            placeholder="O que foi feito ou combinado (ex.: contato com o beneficiário, encaminhado ao programa, auditoria acionada)"
            style={{ width: '100%', fontSize: '0.8rem', padding: 8, borderRadius: 8, border: '1px solid #cfd8dc', resize: 'vertical' }} />
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            <button disabled={salvando} onClick={() => marcar('acompanhando')} style={b('#1565c0')}>Acompanhar</button>
            <button disabled={salvando} onClick={() => marcar('resolvido')} style={b('#2e7d32')}>Resolvido</button>
            <button disabled={salvando} onClick={() => marcar('descartado')} style={b('#546e7a')}>Descartar</button>
          </div>
        </div>
      )}
      {hist && hist.length > 0 && (
        <div style={{ marginTop: 12, fontSize: '0.72rem', color: 'var(--gray-500)' }}>
          {hist.map((h, i) => (
            <div key={i}>{fmtDataHora(h.quando)} · {h.quem} marcou <strong>{ESTADOS[h.estado]?.rot || h.estado}</strong>{h.nota ? `: ${h.nota}` : ''}</div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AbaAlertas({ busca }) {
  const navigate = useNavigate();
  const { temNivel } = useAuth();
  const podeMarcar = temNivel('radar_carteira', 'operador');
  const podeReprocessar = temNivel('radar_carteira', 'gestor');
  const [filtro, setFiltro] = useState({ estado: 'abertos', prioridade: '', sinal: '' });
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [aberto, setAberto] = useState({});
  const [reprocessando, setReprocessando] = useState(false);
  const [prorrog, setProrrog] = useState(null);   // internações prorrogadas da semana (tela própria)
  useEffect(() => { api.get('/radar-carteira/prorrogacoes', { params: { estado: 'abertos' } }).then((r) => setProrrog(r.data.resumo)).catch(() => {}); }, []);

  const carregar = useCallback(() => {
    setLoading(true); setErro(null);
    const params = { estado: filtro.estado, limit: 500 };
    if (filtro.prioridade) params.prioridade = filtro.prioridade;
    if (filtro.sinal) params.sinal = filtro.sinal;
    if (busca) params.busca = busca;
    return alertasCuidadoAPI.listar(params)
      .then((r) => { setDados(r.data); return r.data; })
      .catch((e) => { setDados(null); setErro(e.response?.data?.detail || 'Erro ao carregar os alertas'); })
      .finally(() => setLoading(false));
  }, [filtro, busca]);
  useEffect(() => { carregar(); }, [carregar]);

  const reprocessar = async () => {
    if (reprocessando) return;
    try {
      await alertasCuidadoAPI.reprocessar();
      setReprocessando(true);
      const t0 = dados?.calculado_em;
      const poll = setInterval(async () => {
        const d = await carregar();
        if (d && !d.reprocessando && d.calculado_em !== t0) { clearInterval(poll); setReprocessando(false); }
      }, 5000);
      setTimeout(() => { clearInterval(poll); setReprocessando(false); }, 300000);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao reprocessar'); }
  };

  const chip = (ativo, cor = COR) => ({ fontSize: '0.76rem', fontWeight: 600, padding: '6px 14px', borderRadius: 20, cursor: 'pointer', border: '1.5px solid', background: ativo ? cor : '#fff', color: ativo ? '#fff' : '#546e7a', borderColor: ativo ? cor : '#cfd8dc' });
  const set = (k, v) => setFiltro((f) => ({ ...f, [k]: f[k] === v ? (k === 'estado' ? 'abertos' : '') : v }));
  const r = dados?.resumo || {};
  const itens = dados?.itens || [];

  return (
    <div>
      <div style={{ background: 'linear-gradient(135deg,#fdecea 0%,#f9d6d2 100%)', borderRadius: 12, padding: '1.2rem 1.4rem', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <BellRing size={18} color={COR} />
              <span style={{ fontSize: '0.92rem', fontWeight: 700, color: COR }}>Alertas de Cuidado</span>
              <InfoTip texto={TEXTO_INFO} w={380} />
            </div>
            <p style={{ fontSize: '0.78rem', color: '#37474f', margin: 0, lineHeight: 1.5, maxWidth: 760 }}>
              Quem <strong>mudou de comportamento</strong>: o gasto acelerou contra o próprio histórico, fez exame de investigação ou começou medicamento de alto custo.
              O objetivo é o time agir <strong>antes</strong> de o caso virar problema financeiro. Marque cada alerta como acompanhando, resolvido ou descartado.
            </p>
          </div>
          <div style={{ textAlign: 'right', fontSize: '0.7rem', color: '#8e2a20', whiteSpace: 'nowrap' }}>
            <div><Clock size={11} style={{ verticalAlign: '-1px' }} /> Atualizado em <strong>{fmtDataHora(dados?.calculado_em)}</strong></div>
            <div style={{ marginBottom: 6 }}>Contas até <strong>{fmtData(dados?.data_ref)}</strong></div>
            {podeReprocessar && (
              <button onClick={reprocessar} disabled={reprocessando} style={{ fontSize: '0.72rem', fontWeight: 600, padding: '6px 12px', borderRadius: 8, cursor: reprocessando ? 'default' : 'pointer', border: `1px solid ${COR}`, background: '#fff', color: COR, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <RefreshCw size={13} style={{ animation: reprocessando ? 'spin 1s linear infinite' : 'none' }} />
                {reprocessando ? 'Recalculando…' : 'Recalcular agora'}
              </button>
            )}
          </div>
        </div>
      </div>

      {prorrog && (
        <button onClick={() => navigate('/internacoes-prorrogadas')}
                style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 12, background: '#fff', border: `1.5px solid ${COR}`, borderRadius: 12, padding: '12px 16px', marginBottom: '1rem', cursor: 'pointer' }}>
          <BedDouble size={22} color={COR} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, color: COR, fontSize: '0.9rem' }}>Internações prorrogadas nesta semana: {prorrog.internacoes}</div>
            <div style={{ fontSize: '0.76rem', color: '#546e7a' }}>
              {prorrog.internacoes > 0
                ? <>{prorrog.prorrogacoes} prorrogação(ões) · {prorrog.muitas} com 3 ou mais · {prorrog.abertas} em aberto. Clique para ver o detalhe e acompanhar.</>
                : <>Nenhuma pedida até agora nesta semana. Clique para ver as semanas anteriores.</>}
            </div>
          </div>
          <ExternalLink size={15} color={COR} />
        </button>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.78rem', color: 'var(--gray-500)', fontWeight: 600, minWidth: 70 }}>Situação:</span>
        {[['abertos', `Em aberto (${(r.novo || 0) + (r.acompanhando || 0)})`], ['novo', `Novos (${r.novo || 0})`],
          ['acompanhando', `Acompanhando (${r.acompanhando || 0})`], ['resolvido', `Resolvidos (${r.resolvido || 0})`],
          ['descartado', `Descartados (${r.descartado || 0})`], ['todos', 'Todos']].map(([v, l]) => (
          <button key={v} onClick={() => setFiltro((f) => ({ ...f, estado: v }))} style={chip(filtro.estado === v, '#37474f')}>{l}</button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.78rem', color: 'var(--gray-500)', fontWeight: 600, minWidth: 70 }}>Filtrar:</span>
        <button onClick={() => set('prioridade', 'Alta')} style={chip(filtro.prioridade === 'Alta')}>Prioridade Alta ({r.alta || 0})</button>
        <button onClick={() => set('prioridade', 'Média')} style={chip(filtro.prioridade === 'Média', '#e65100')}>Média ({r.media || 0})</button>
        {Object.entries(SINAIS).map(([k, s]) => (
          <button key={k} onClick={() => set('sinal', k)} style={chip(filtro.sinal === k, s.cor)}>{s.rot} ({r[k] || 0})</button>
        ))}
      </div>

      {loading && <div style={{ padding: 20, color: 'var(--gray-500)', display: 'flex', gap: 8, alignItems: 'center' }}><Loader size={16} /> Carregando…</div>}
      {erro && <div style={{ padding: 20, color: '#e74c3c' }}>{erro}</div>}
      {!loading && !erro && dados && !dados.calculado_em && (
        <div style={{ padding: 20, color: 'var(--gray-500)' }}>Os alertas ainda não foram calculados. O cálculo roda todo dia depois da carga das contas{podeReprocessar ? ', ou clique em "Recalcular agora".' : '.'}</div>
      )}
      {!loading && !erro && dados?.calculado_em && (
        <>
          <div style={{ fontSize: '0.74rem', color: 'var(--gray-500)', marginBottom: 10 }}>
            <strong style={{ color: COR }}>{dados.total_filtrado}</strong> alerta(s) nesta seleção{busca ? ` para "${busca}"` : ''}
            {r.voltaram ? ` · ${r.voltaram} voltaram depois de descartados (sinal novo)` : ''}
          </div>
          {itens.length === 0 ? (
            <div style={{ padding: 20, color: 'var(--gray-500)' }}>Nenhum alerta nesta seleção.</div>
          ) : itens.map((a) => {
            const ab = !!aberto[a.matricula];
            const est = ESTADOS[a.estado] || ESTADOS.novo;
            return (
              <div key={a.matricula} style={{ border: '1px solid #e0e0e0', borderLeft: `4px solid ${a.prioridade === 'Alta' ? COR : '#e67e22'}`, borderRadius: 10, marginBottom: 8, overflow: 'hidden', background: '#fff' }}>
                <div onClick={() => setAberto((p) => ({ ...p, [a.matricula]: !p[a.matricula] }))} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', cursor: 'pointer', background: ab ? '#fffafa' : '#fff' }}>
                  <span style={{ fontSize: '0.66rem', fontWeight: 700, color: a.prioridade === 'Alta' ? '#b71c1c' : '#e65100', background: a.prioridade === 'Alta' ? '#ffebee' : '#fff3e0', padding: '3px 9px', borderRadius: 6, whiteSpace: 'nowrap' }}>{a.prioridade}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.84rem', color: 'var(--gray-800)', display: 'flex', alignItems: 'center', gap: 6 }}>
                      {a.nome}
                      {a.cpf && (
                        <button title="Abrir a ficha" onClick={(e) => { e.stopPropagation(); navigate(`/beneficiario-detalhe/${a.cpf}`); }}
                          style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--primary)', padding: 0, display: 'inline-flex' }}>
                          <ExternalLink size={13} />
                        </button>
                      )}
                    </div>
                    <div style={{ fontSize: '0.68rem', color: 'var(--gray-400)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {a.empresa} · Mat. {a.matricula} · último sinal {fmtData(a.ultimo_sinal)}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end', maxWidth: 330 }}>
                    {a.sinais.map((s) => <Selo key={s} s={s} />)}
                  </div>
                  <span style={{ fontSize: '0.64rem', fontWeight: 700, color: est.cor, background: est.bg, padding: '2px 8px', borderRadius: 10, whiteSpace: 'nowrap' }}>
                    {est.rot}{a.voltou ? ' · voltou' : ''}
                  </span>
                  {ab ? <ChevronUp size={16} color="#90a4ae" /> : <ChevronDown size={16} color="#90a4ae" />}
                </div>
                {ab && (
                  <div style={{ padding: '12px 14px', borderTop: '1px solid #eee', background: '#fffafa' }}>
                    <Acoes a={a} podeMarcar={podeMarcar} onSalvo={carregar} />
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}
